if (!customElements.get('cart-sticky-bar')) {
  customElements.define(
    'cart-sticky-bar',
    class CartStickyBar extends HTMLElement {
      constructor() {
        super();
        this.panel = this.querySelector('.cart-sticky-bar__panel');
        this.closeBtn = this.querySelector('.cart-sticky-bar__close');
        this.thumbsEl = this.querySelector('.cart-sticky-bar__thumbs');
        this.statusEl = this.querySelector('.cart-sticky-bar__status');
        this.totalLabelEl = this.querySelector('.cart-sticky-bar__total-label');
        this.totalAmountEl = this.querySelector('.cart-sticky-bar__total-amount');
        this.ctaTextEl = this.querySelector('.cart-sticky-bar__cta-text');

        this.statusOneTemplate = this.dataset.statusOne || '[quantity] item added to cart';
        this.statusOtherTemplate = this.dataset.statusOther || '[quantity] items added to cart';
        this.totalLabel = this.dataset.totalLabel || 'Total:';
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.viewCartLabel = this.dataset.viewCart || 'View your cart';
        this.pulseTimer = null;
        this.dismissed = false;
        this.handleViewportChange = this.updateFloatingButtonOffset.bind(this);
      }

      connectedCallback() {
        if (this.totalLabelEl && !this.totalLabelEl.textContent.trim()) {
          this.totalLabelEl.textContent = this.totalLabel;
        }
        if (this.ctaTextEl) this.ctaTextEl.textContent = this.viewCartLabel;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'cart-sticky-bar') return;
          const fromAdd =
            event?.source === 'product-form' ||
            event?.source === 'product-buy-row-form' ||
            event?.source === 'product-buy-row' ||
            event?.source === 'product-bundle-upsell' ||
            event?.source === 'product-bundle-offer' ||
            event?.source === 'quick-add' ||
            event?.source === 'product-short-videos' ||
            event?.source === 'ymal-card-form' ||
            event?.source === 'cart-drawer-ymal';
          this.onCartUpdate(event, fromAdd);
        });

        this.closeBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.dismissed = true;
          this.hide();
        });

        this.panel?.addEventListener('click', this.onViewCart.bind(this));

        window.addEventListener('resize', this.handleViewportChange);
        if ('ResizeObserver' in window && this.panel) {
          this.panelResizeObserver = new ResizeObserver(() => {
            if (this.classList.contains('is-visible')) this.updateFloatingButtonOffset();
          });
          this.panelResizeObserver.observe(this.panel);
        }

        this.syncFromCart({ pulse: false });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        clearTimeout(this.pulseTimer);
        window.removeEventListener('resize', this.handleViewportChange);
        this.panelResizeObserver?.disconnect();
      }

      onViewCart() {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartIcon = document.querySelector('#cart-icon-bubble');

        if (cartDrawer) {
          cartDrawer.open(cartIcon || this.panel);
          return;
        }

        window.location.href = window.routes?.cart_url || '/cart';
      }

      async onCartUpdate(event, fromAdd = false) {
        let cart = event?.cartData;
        if (!this.isFullCart(cart)) {
          cart = await this.fetchCart();
        }
        if (fromAdd) this.dismissed = false;
        this.applyCart(cart, fromAdd);
      }

      async syncFromCart({ pulse = false } = {}) {
        const cart = await this.fetchCart();
        this.applyCart(cart, pulse);
      }

      applyCart(cart, fromAdd = false) {
        const count = this.resolveItemCount(cart);

        if (!count || count < 1) {
          this.hide();
          return;
        }

        this.updateUI(cart, count);

        /*
          After add-to-cart:
          - Auto-open ON → drawer opens; keep sticky bar hidden
          - Auto-open OFF (default) → show bottom “View your cart” bar
        */
        if (fromAdd) {
          if (window.themeSettings?.cartDrawerAutoOpen) {
            this.hide();
            return;
          }
          this.dismissed = false;
          this.show(true);
          return;
        }

        if (this.dismissed) return;
        this.show(false);
      }

      isFullCart(cart) {
        return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number';
      }

      resolveItemCount(cart) {
        if (!cart) return 0;
        if (typeof cart.item_count === 'number') return cart.item_count;
        if (Array.isArray(cart.items)) {
          return cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        }
        return 0;
      }

      fetchCart() {
        return fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
      }

      formatStatus(count) {
        const template = count === 1 ? this.statusOneTemplate : this.statusOtherTemplate;
        return template.replace('[quantity]', String(count));
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') return '';

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, this.moneyFormat);
        }

        const amount = (cents / 100).toFixed(2);
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
      }

      itemImageUrl(item) {
        let url = item.image || item.featured_image?.url || '';
        if (!url) return '';
        if (url.startsWith('//')) url = `https:${url}`;
        const joiner = url.includes('?') ? '&' : '?';
        return `${url}${joiner}width=140`;
      }

      renderThumbs(items) {
        if (!this.thumbsEl || !Array.isArray(items)) return;
        if (this.dataset.showImages === 'false') {
          this.thumbsEl.dataset.stack = '0';
          this.thumbsEl.innerHTML = '';
          return;
        }

        const stackItems = items.slice(-3);
        const stackCount = Math.min(stackItems.length, 3);

        this.thumbsEl.dataset.stack = String(stackCount);
        this.thumbsEl.innerHTML = '';

        stackItems.forEach((item) => {
          const src = this.itemImageUrl(item);
          if (!src) return;

          const img = document.createElement('img');
          img.className = 'cart-sticky-bar__thumb';
          img.src = src;
          img.alt = '';
          img.width = 72;
          img.height = 72;
          img.loading = 'lazy';
          this.thumbsEl.appendChild(img);
        });

        if (!this.thumbsEl.querySelector('img')) {
          this.thumbsEl.dataset.stack = '0';
        }
      }

      updateUI(cart, count) {
        if (this.statusEl) this.statusEl.textContent = this.formatStatus(count);
        if (this.totalLabelEl) this.totalLabelEl.textContent = this.totalLabel;
        if (this.totalAmountEl && this.dataset.showTotal !== 'false') {
          this.totalAmountEl.textContent = this.formatMoney(cart.total_price);
        }
        this.renderThumbs(cart.items || []);
      }

      updateFloatingButtonOffset() {
        if (!this.panel || !this.classList.contains('is-visible')) return;

        const styles = window.getComputedStyle(this);
        const panelHeight = this.panel.offsetHeight || 0;
        const bottom = parseFloat(styles.bottom) || 0;
        const paddingBottom = parseFloat(styles.paddingBottom) || 0;
        const requiredBottom = bottom + paddingBottom + panelHeight + 12;

        document.body.style.setProperty('--cart-sticky-bar-wa-bottom', `${requiredBottom}px`);
      }

      show(pulse = false) {
        this.removeAttribute('hidden');
        this.updateFloatingButtonOffset();

        requestAnimationFrame(() => {
          this.classList.add('is-visible');
          document.body.classList.add('cart-sticky-bar-visible');
        });

        if (pulse) {
          this.classList.remove('is-pulse');
          void this.offsetWidth;
          this.classList.add('is-pulse');
          clearTimeout(this.pulseTimer);
          this.pulseTimer = setTimeout(() => this.classList.remove('is-pulse'), 500);
        }
      }

      hide() {
        this.classList.remove('is-visible', 'is-pulse');
        document.body.classList.remove('cart-sticky-bar-visible');
        document.body.style.removeProperty('--cart-sticky-bar-wa-bottom');
        this.setAttribute('hidden', '');
      }
    }
  );
}
